import React from "react";
import { 
  TrendingUp, 
  Shield, 
  Gauge, 
  Users,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";

const ValueBenefitsSection = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const benefits = [
    {
      icon: TrendingUp,
      title: currentLanguage === 'de' ? 'Transparenz' : 'Transparency',
      description: currentLanguage === 'de'
        ? 'KI ist nicht mehr „Hoffnung", sondern steuerbarer Werttreiber mit messbarem EBIT-Impact.'
        : 'AI is no longer "hope", but a controllable value driver with measurable EBIT impact.',
      metrics: currentLanguage === 'de' ? 'Ø €1.2M EBIT-Impact' : 'Avg €1.2M EBIT Impact'
    },
    {
      icon: Shield,
      title: currentLanguage === 'de' ? 'Investitionsschutz' : 'Investment Protection',
      description: currentLanguage === 'de'
        ? 'Weniger Tool-Aktionismus, mehr Portfolio-Logik. Jeder Euro wird nachvollziehbar eingesetzt.'
        : 'Less tool activism, more portfolio logic. Every euro is spent traceably.',
      metrics: currentLanguage === 'de' ? '4.8x ROI' : '4.8x ROI'
    },
    {
      icon: Gauge,
      title: currentLanguage === 'de' ? 'Skalierung' : 'Scaling',
      description: currentLanguage === 'de'
        ? 'Aus 2 Piloten werden 20 Rollouts – ohne Chaos, mit klaren Gates und Templates.'
        : 'From 2 pilots to 20 rollouts – without chaos, with clear gates and templates.',
      metrics: currentLanguage === 'de' ? '14 Use Cases aktiv' : '14 Use Cases active'
    },
    {
      icon: Users,
      title: currentLanguage === 'de' ? 'Adoption' : 'Adoption',
      description: currentLanguage === 'de'
        ? 'Teams sehen Fortschritt, Führung sieht Wirkung. Messbare Nutzung statt PowerPoint-KI.'
        : 'Teams see progress, leadership sees impact. Measurable usage instead of PowerPoint AI.',
      metrics: currentLanguage === 'de' ? '78% Adoption Rate' : '78% Adoption Rate'
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-brand-spread border-y border-hairline">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-[64ch] mb-16">
          <span className="inline-block bg-brand/10 text-brand-300 border border-brand-400/25 px-3 py-1.5 rounded-full text-xs font-medium mb-5">
            {currentLanguage === 'de' ? 'Ihre Ergebnisse' : 'Your Results'}
          </span>
          <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-5">
            {currentLanguage === 'de' 
              ? 'Was Sie mit BROVKO erreichen'
              : 'What you achieve with BROVKO'
            }
          </h2>
          <p className="text-[1.05rem] leading-relaxed text-ink-muted max-w-[62ch]">
            {currentLanguage === 'de'
              ? 'Die KI-Value-Engine liefert messbare Ergebnisse – nicht in 90 Tagen, sondern nachhaltig und im Tempo Ihrer Organisation.'
              : 'The AI Value Engine delivers measurable results – not in 90 days, but sustainably and at the pace of your organization.'
            }
          </p>
        </div>

        {/* Der inszenierte Moment: grosse Aussagen mit der Kennzahl im Satz,
            nicht als Kachel daneben. Haarlinien statt Kartenraendern. */}
        <div className="border-t border-white/20">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="grid gap-4 border-b border-white/20 py-9 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] md:items-baseline md:gap-12"
            >
              <p className="max-w-[20ch] text-[clamp(1.45rem,2.8vw,2.35rem)] font-bold leading-[1.14] tracking-[-0.038em] text-white">
                {benefit.title}
                <span className="text-brand-300">{" \u00b7 "}{benefit.metrics}</span>
              </p>
              <p className="max-w-[46ch] text-[0.98rem] leading-relaxed text-ink-muted">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
       {/* CTA */}
        <div className="text-center">
          <Button 
            size="lg"
            onClick={() => navigate('/ki-value-check')}
            className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            {currentLanguage === 'de' ? 'KI-Value Check starten' : 'Start AI Value Check'}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-ink-faint text-xs mt-3">
            {currentLanguage === 'de' 
              ? '7 Fragen • 3 Minuten • Personalisierte Analyse'
              : '7 Questions • 3 Minutes • Personalized Analysis'
            }
          </p>
        </div>
      </div>
    </section>
  );
};

export default ValueBenefitsSection;
